import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import TextField from "@mui/material/TextField";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { listPolicyAttributes, updatePlanText, type RadGroup, type RadgroupAttribute } from "../../api/catalog";
import { listPriceComponents, listPriceRules, listSellerGst, priceWords, sellerGstOf, speaksGst } from "../../api/priceList";
import { planComponent, planDetail } from "../../api/planDetail";
import { canOpenCreatePlan } from "../../api/planBuilder";
import { CREATE_PLAN_PATH, PRICE_LIST_PATH } from "../../components/nav";
import { ChangePriceDialog } from "./PriceList";

const day = (iso?: string) => (iso ? iso.slice(0, 10) : "");

/**
 * Plans › On sale › Details (owner 2026-10-15): one plan as the operator sells
 * it -- limits, price and its history, network settings, the add-ons it sells
 * with. "Change the price" opens Change price on the plan's own open price
 * (owner 2026-10-28: it opened the price-file upload instead); changing speed,
 * days or devices is a new plan, so a plan never sells at one band and runs
 * at another.
 */
export function PlanDetails({ ns, group }: { ns: string; group: RadGroup }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);
  const [editing, setEditing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [heading, setHeading] = useState(group.group_hd);
  const [description, setDescription] = useState(group.group_desc);
  const save = useMutation<unknown, Error>({
    mutationFn: () => updatePlanText(identity, { ...group, namespace: ns }, heading.trim(), description.trim()),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["plans-on-sale", ns] });
    },
  });
  const components = useQuery({
    queryKey: ["plan-detail-components", ns],
    enabled,
    retry: false,
    queryFn: ({ signal }) => listPriceComponents(identity, ns, signal),
  });
  const rules = useQuery({
    queryKey: ["plan-detail-rules", ns],
    enabled,
    retry: false,
    queryFn: ({ signal }) => listPriceRules(identity, ns, signal),
  });
  // Owner 2026-10-29: the seller of record's GST, as billing's quote states it.
  const sellerGst = useQuery({
    queryKey: ["pricelist-gst", ns],
    enabled,
    retry: false,
    queryFn: ({ signal }) => listSellerGst(identity, ns, signal),
  });
  const network = useQuery({
    queryKey: ["plan-detail-network", ns, group.groupname],
    enabled,
    retry: false,
    queryFn: async () => {
      const [check, reply] = await Promise.all([
        listPolicyAttributes(identity, "check", ns, group.groupname),
        listPolicyAttributes(identity, "reply", ns, group.groupname),
      ]);
      return [...(check.data ?? []), ...(reply.data ?? [])] as RadgroupAttribute[];
    },
  });
  if (components.isLoading || rules.isLoading) return <Typography variant="body2">Loading details…</Typography>;
  if (components.error || rules.error)
    return <Alert severity="error">Details not loaded: {((components.error ?? rules.error) as Error).message}</Alert>;
  const c0 = planComponent(group, components.data ?? []);
  const gst = c0 ? sellerGstOf(c0, sellerGst.data) : undefined;
  const speak = speaksGst(sellerGst.data);
  const d = planDetail(group, components.data ?? [], rules.data ?? [], new Date(), gst);
  const noNetwork = network.error instanceof ApiError && network.error.status === 403;

  return (
    <Box sx={{ py: 1 }} aria-label={`${group.groupname} details`}>
      {editing ? (
        <Stack spacing={1} sx={{ mb: 1, maxWidth: 560 }}>
          <TextField size="small" label="Plan name" value={heading} onChange={(e) => setHeading(e.target.value)} />
          <TextField size="small" label="Description" value={description} multiline minRows={2}
            onChange={(e) => setDescription(e.target.value)} />
          {save.isError && <Alert severity="error">Not saved: {save.error.message}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={save.isPending || heading.trim().length < 3}
              onClick={() => save.mutate()}>
              Save
            </Button>
            <Button size="small" onClick={() => { setEditing(false); setHeading(group.group_hd); setDescription(group.group_desc); }}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2">{group.group_desc || "No description."}</Typography>
          <Button size="small" onClick={() => { save.reset(); setEditing(true); }}>
            Edit name and description
          </Button>
        </Stack>
      )}
      {!d.component ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          This plan has no price item, so it cannot be sold. Build plans from the price list under Plans › Create plan.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{d.component.name}</strong>
            {d.speedMbps ? ` · ${d.speedMbps} Mbps` : ""}
            {d.termDays ? ` · ${d.termDays} days` : ""}
            {d.devices ? ` · ${d.devices} device${d.devices === "1" ? "" : "s"}` : ""}
            {d.product ? ` · ${d.product}` : ""}
          </Typography>
          <Typography variant="subtitle2">{speak ? "Price (with GST)" : "Price"}</Typography>
          {d.history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No price rule; the item's own price applies.</Typography>
          ) : (
            <Table size="small" aria-label={`${group.groupname} price history`} sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>Until</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Set by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.history.map((h) => (
                  <TableRow key={`${h.from}-${h.source}`}>
                    <TableCell>{day(h.from)}</TableCell>
                    <TableCell>{h.to ? day(h.to) : "—"}</TableCell>
                    <TableCell align="right">{h.gross === undefined ? "—" : priceWords(h.gross, gst ?? 0, speak)}</TableCell>
                    <TableCell>{h.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {d.addons.length > 0 && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Sells with:</strong> {d.addons.map((a) => a.name).join(", ")}
            </Typography>
          )}
        </>
      )}
      <Typography variant="subtitle2">Network settings</Typography>
      {noNetwork ? (
        <Typography variant="body2" color="text.secondary">Your role does not show network settings.</Typography>
      ) : network.isLoading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : network.error ? (
        <Alert severity="error">Network settings not loaded: {(network.error as Error).message}</Alert>
      ) : (
        <Table size="small" aria-label={`${group.groupname} network settings`} sx={{ mb: 1 }}>
          <TableBody>
            {(network.data ?? []).map((a, i) => (
              <TableRow key={`${a.attribute}-${i}`}>
                <TableCell>
                  <code>{a.attribute}</code>
                </TableCell>
                <TableCell>{a.op}</TableCell>
                <TableCell>
                  <code>{a.value}</code>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {canOpenCreatePlan(identity.roles) && d.component?.active && d.changeable && gst !== undefined ? (
          <Button size="small" variant="outlined" onClick={() => setChanging(true)}>
            Change the price
          </Button>
        ) : (
          <Button size="small" variant="outlined" component={RouterLink} to={PRICE_LIST_PATH}>
            Open the price list
          </Button>
        )}
        <Button size="small" variant="outlined" component={RouterLink} to={CREATE_PLAN_PATH}>
          New plan with other limits
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        A price changes from a date you choose, so old invoices stay as they were. Speed, days and devices never change
        on a plan in use: make a new plan and close this one to new sales.
      </Typography>
      {changing && d.changeable && d.component && (
        gst !== undefined && <ChangePriceDialog ns={ns} rule={d.changeable} gst={gst} speak={speak} onClose={() => setChanging(false)} />
      )}
    </Box>
  );
}
